'use client';

import React from 'react';
import { FaExclamationTriangle, FaRedo, FaHome } from 'react-icons/fa';
import Link from 'next/link';

/**
 * Error Boundary component for eSIM section
 * Catches JavaScript errors anywhere in the child component tree
 */
class ESimErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        };
    }

    static getDerivedStateFromError(_error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Log error to console and potentially to error reporting service
        console.error('eSIM Error Boundary caught an error:', error, errorInfo);

        this.setState({
            error: error,
            errorInfo: errorInfo
        });

        // TODO: Send error to monitoring service (Sentry, LogRocket, etc.)
        // Example:
        // if (typeof window !== 'undefined' && window.Sentry) {
        //     window.Sentry.captureException(error, {
        //         contexts: {
        //             react: {
        //                 componentStack: errorInfo.componentStack
        //             }
        //         }
        //     });
        // }
    }

    handleRetry = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null
        });
    };

    render() {
        if (this.state.hasError) {
            const { lang = 'ru', countrySlug = 'montenegro' } = this.props;

            const translations = {
                ru: {
                    title: 'Что-то пошло не так',
                    subtitle: 'Произошла ошибка при загрузке eSIM раздела',
                    description: 'Попробуйте обновить страницу или вернуться на главную',
                    retry: 'Попробовать снова',
                    home: 'На главную',
                    backToEsim: 'Назад к eSIM',
                    errorDetails: 'Детали ошибки (для разработчиков)',
                    showDetails: 'Показать детали',
                    hideDetails: 'Скрыть детали'
                },
                en: {
                    title: 'Something went wrong',
                    subtitle: 'An error occurred while loading the eSIM section',
                    description: 'Try refreshing the page or return to the home page',
                    retry: 'Try again',
                    home: 'Go home',
                    backToEsim: 'Back to eSIM',
                    errorDetails: 'Error details (for developers)',
                    showDetails: 'Show details',
                    hideDetails: 'Hide details'
                }
            };

            const t = translations[lang];

            return (
                <div className="min-h-screen bg-gradient-to-br from-red-50 via-red-50 to-red-100 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl w-full text-center">
                        {/* Error Icon */}
                        <div className="mb-6">
                            <FaExclamationTriangle className="text-red-500 text-6xl mx-auto mb-4" />
                        </div>

                        {/* Error Message */}
                        <h1 className="text-3xl font-bold text-gray-800 mb-4">
                            {t.title}
                        </h1>

                        <p className="text-xl text-gray-600 mb-2">
                            {t.subtitle}
                        </p>

                        <p className="text-gray-500 mb-8">
                            {t.description}
                        </p>

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                            <button
                                onClick={this.handleRetry}
                                className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium shadow-lg hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-2"
                                aria-label={t.retry}
                            >
                                <FaRedo className="w-4 h-4" />
                                {t.retry}
                            </button>

                            <Link
                                href={`/${lang}/${countrySlug}/esim`}
                                className="inline-flex items-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-colors font-medium shadow-lg hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-600 focus-visible:ring-offset-2"
                                aria-label={t.backToEsim}
                            >
                                <FaHome className="w-4 h-4" />
                                {t.backToEsim}
                            </Link>

                            <Link
                                href={`/${lang}`}
                                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium shadow-lg hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
                                aria-label={t.home}
                            >
                                <FaHome className="w-4 h-4" />
                                {t.home}
                            </Link>
                        </div>

                        {/* Error Details (for development) */}
                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details className="mt-8 text-left">
                                <summary className="cursor-pointer text-sm font-medium text-gray-600 hover:text-gray-800 mb-2">
                                    {t.errorDetails}
                                </summary>
                                <div className="bg-gray-100 rounded-lg p-4 text-xs font-mono text-gray-700 overflow-auto max-h-40">
                                    <div className="mb-2">
                                        <strong>Error:</strong> {this.state.error.toString()}
                                    </div>
                                    {this.state.errorInfo && (
                                        <div>
                                            <strong>Component Stack:</strong>
                                            <pre className="whitespace-pre-wrap mt-1">
                                                {this.state.errorInfo.componentStack}
                                            </pre>
                                        </div>
                                    )}
                                </div>
                            </details>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ESimErrorBoundary;